import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import hide from 'src/rest/requests/classifieds/hide'
import { actions, selectors } from '..'
import { MediaType } from '../../form/fields/FormFieldUploadMedia'
import { isEmptyFile } from '../../helpers/FileHelpers'
import {
  getStrapiUploadFirstFileId,
  strapiRelationConnect,
} from '../../helpers/strapiRest'
import addMedia from '../../rest/requests/classifieds/addMedia'
import archive from '../../rest/requests/classifieds/archive'
import classifiedById from '../../rest/requests/classifieds/classifiedById'
import create from '../../rest/requests/classifieds/create'
import deleteMedia from '../../rest/requests/classifieds/deleteMedia'
import getBlockSchema from '../../rest/requests/classifieds/getBlockSchema'
import getByUser from '../../rest/requests/classifieds/getByUserId'
import getCabinetTypes from '../../rest/requests/classifieds/getCabinetTypes'
import getCategories from '../../rest/requests/classifieds/getCategories'
import getCategoriesByUserProfile from '../../rest/requests/classifieds/getCategoriesByUserProfile'
import getClassifieds from '../../rest/requests/classifieds/getClassifieds'
import getClassifiedsWithMeta from '../../rest/requests/classifieds/getClassifiedsWithMeta'
import getLatestClassifiedsByCategory from '../../rest/requests/classifieds/getLatestClassifiedsByCategory'
import getMediaList from '../../rest/requests/classifieds/getMediaList'
import getOfferTypes from '../../rest/requests/classifieds/getOfferTypes'
import getSecretariats from '../../rest/requests/classifieds/getSecretariats'
import getSpecialties from '../../rest/requests/classifieds/getSpecialties'
import increaseView from '../../rest/requests/classifieds/increaseView'
import publish from '../../rest/requests/classifieds/publish'
import update from '../../rest/requests/classifieds/update'
import updateMedia from '../../rest/requests/classifieds/updateMedia'
import upload from '../../rest/requests/media/upload'
import GetContentTypeSchema from '../../rest/requests/content-types-builder/getContentTypeSchema'
import { type Classified, Status } from '../../rest/types/Classified'
import { User } from '../../rest/types/User'
import tracking from '../../tracking'
import ApiSagas from '../api/sagas'

import { adsFormValuesTransformer } from './transformers'
import remove from 'src/rest/requests/classifieds/remove'
import getKeywords from 'src/rest/requests/classifieds/getKeywords'

export default class ClassifiedsSagas {
  static *onCreateRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.createRequest>) {
    const response = yield* ApiSagas.rest(create(payload))

    if (response.data) {
      yield put(actions.classifieds.createSuccess(response.data))
    } else {
      yield put(actions.classifieds.createError(response.errors))
    }
  }

  static *onArchiveRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.archiveRequest>) {
    const response = yield* ApiSagas.rest(archive(payload))

    if (response.data) {
      yield put(actions.classifieds.archiveSuccess(response.data))
      yield call(tracking.ad_archive, response.data)
    } else {
      yield put(actions.classifieds.archiveError(response.errors))
    }
  }

  static *onHideRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.hideRequest>) {
    const response = yield* ApiSagas.rest(hide(payload))

    if (response.data) {
      yield put(actions.classifieds.hideSuccess(response.data))
      yield call(tracking.ad_hide, response.data)
    } else {
      yield put(actions.classifieds.hideError(response.errors))
    }
  }

  static *onRemoveRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.removeRequest>) {
    const response = yield* ApiSagas.rest(remove(payload))

    // Strapi 5 DELETE returns 204 No Content: response.data is always empty ('' with axios)
    if (!response.errors) {
      const removed = response.data || (payload as unknown as Classified)
      yield put(actions.classifieds.removeSuccess(removed))
      yield call(tracking.ad_remove, removed)
    } else {
      // 403 = missing role permission or not the owner, shown with a dedicated message by useRemoveAds
      yield put(
        actions.classifieds.removeError(
          response.status === 403 ? [{ message: 'forbidden' }] : response.errors
        )
      )
    }
  }

  static *onUpdateRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.updateRequest>) {
    const user: ReturnType<typeof selectors.auth.user> = yield select(
      selectors.auth.user
    )

    const selectedAddress: ReturnType<
      typeof selectors.classifieds.selectedAddress
    > = yield select(selectors.classifieds.selectedAddress)

    const categories: ReturnType<typeof selectors.classifieds.categories> =
      yield select(selectors.classifieds.categories)

    if (user && categories?.length) {
      const values: ReturnType<typeof adsFormValuesTransformer> = yield call(
        adsFormValuesTransformer,
        payload,
        categories,
        user,
        selectedAddress
      )

      const data: any = {
        ...values.data,
        user: user.id,
        ...(payload?.classifiedStatus !== undefined && {
          classifiedStatus: payload.classifiedStatus,
        }),
        // Without a newly picked address, keep the stored _geoloc instead of wiping it
        ...(selectedAddress && {
          _geoloc: {
            lat: selectedAddress.lat,
            lng: selectedAddress.long,
          },
        }),
      }

      if (values.files?.length) {
        for (const file of values.files) {
          const uploadResponse = yield* ApiSagas.rest(
            upload({ files: file.value })
          )
          const uploadedId = getStrapiUploadFirstFileId(uploadResponse?.data)
          if (!uploadedId) {
            yield put(
              actions.classifieds.updateError(
                uploadResponse?.errors ?? [{ message: 'File upload failed' }]
              )
            )
            return
          }
          const path = file.name.replace(/^files\./, '').split('.')
          let cursor = data
          for (let i = 0; i < path.length - 1; i++) {
            const key = path[i]
            cursor[key] = { ...(cursor[key] ?? {}) }
            cursor = cursor[key]
          }
          cursor[path[path.length - 1]] = uploadedId
        }
      }

      const response = yield* ApiSagas.rest(
        update({
          id: (payload as any).id,
          data,
        })
      )
      if (response.data && !response.data?.error) {
        yield put(actions.classifieds.updateSuccess(response.data))
        if (payload?.classifiedStatus === Status.Published) {
          yield call(tracking.ad_creation, response.data)
        }
      } else {
        yield put(actions.classifieds.updateError(response.errors))
      }
    }
  }

  static *onGetClassifiedById({
    payload,
  }: ReturnType<typeof actions.classifieds.getClassifiedByIdRequest>) {
    const response = yield* ApiSagas.rest(classifiedById(payload))

    if (response.data && !response.data?.error) {
      yield put(actions.classifieds.getClassifiedByIdSuccess(response.data))
    } else {
      yield put(actions.classifieds.getClassifiedByIdError(response.errors))
    }
  }

  static *onGetClassifieds({
    payload,
  }: ReturnType<typeof actions.classifieds.getClassifiedsRequest>) {
    const response = yield* ApiSagas.rest(getClassifieds(payload))
    if (response.data && !response.data?.error) {
      yield put(actions.classifieds.getClassifiedsSuccess(response.data))
    } else {
      yield put(actions.classifieds.getClassifiedsError(response.errors))
    }
  }

  static *onGetClassifiedsWithMeta({
    payload,
  }: ReturnType<typeof actions.classifieds.getClassifiedsWithMetaRequest>) {
    const response = yield* ApiSagas.rest(getClassifiedsWithMeta(payload))
    if (response.data && !response.data?.error) {
      yield put(
        actions.classifieds.getClassifiedsWithMetaSuccess(response.data)
      )
    } else {
      yield put(
        actions.classifieds.getClassifiedsWithMetaError(response.errors)
      )
    }
  }

  static *onGetPublishAds({
    payload,
  }: ReturnType<typeof actions.classifieds.getPublishAdsRequest>) {
    const response = yield* ApiSagas.rest(getClassifieds(payload))
    if (response.data && !response.data?.error) {
      yield put(actions.classifieds.getPublishAdsSuccess(response.data))
    } else {
      yield put(actions.classifieds.getPublishAdsError(response.errors))
    }
  }

  static *onGetLatestClassifiedsByCategoryByRequest({
    payload,
  }: ReturnType<
    typeof actions.classifieds.getLatestClassifiedsByCategoryRequest
  >) {
    const response = yield* ApiSagas.rest(
      getLatestClassifiedsByCategory(payload)
    )

    if (response.data) {
      yield put(
        actions.classifieds.getLatestClassifiedsByCategorySuccess(response.data)
      )
    } else {
      yield put(
        actions.classifieds.getLatestClassifiedsByCategoryError(response.errors)
      )
    }
  }

  static *onGetCategoriesRequest() {
    const response = yield* ApiSagas.rest(getCategories())

    if (response.data) {
      yield put(actions.classifieds.getCategoriesSuccess(response.data))
    } else {
      yield put(actions.classifieds.getCategoriesError(response.errors))
    }
  }

  static *onGetCategoriesByUserProfileRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.getCategoriesByUserProfileRequest>) {
    const response = yield* ApiSagas.rest(getCategoriesByUserProfile(payload))

    if (response.data) {
      yield put(
        actions.classifieds.getCategoriesByUserProfileSuccess(response.data)
      )
    } else {
      yield put(
        actions.classifieds.getCategoriesByUserProfileError(response.errors)
      )
    }
  }

  static *onGetCabinetTypesRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.getCabinetTypesRequest>) {
    const response = yield* ApiSagas.rest(getCabinetTypes(payload))

    if (response.data) {
      yield put(actions.classifieds.getCabinetTypesSuccess(response.data))
    } else {
      yield put(actions.classifieds.getCabinetTypesError(response.errors))
    }
  }

  static *onGetOfferTypesRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.getOfferTypesRequest>) {
    const response = yield* ApiSagas.rest(getOfferTypes(payload))

    if (response.data) {
      yield put(actions.classifieds.getOfferTypesSuccess(response.data))
    } else {
      yield put(actions.classifieds.getOfferTypesError(response.errors))
    }
  }

  static *onGetSpecialtiesRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.getSpecialtiesRequest>) {
    const response = yield* ApiSagas.rest(getSpecialties(payload))

    if (response.data) {
      yield put(actions.classifieds.getSpecialtiesSuccess(response.data))
    } else {
      yield put(actions.classifieds.getSpecialtiesError(response.errors))
    }
  }

  static *onGetSecretariatsRequests({
    payload,
  }: ReturnType<typeof actions.classifieds.getSecretariatsRequest>) {
    const response = yield* ApiSagas.rest(getSecretariats(payload))

    if (response.data) {
      yield put(actions.classifieds.getSecretariatsSuccess(response.data))
    } else {
      yield put(actions.classifieds.getSecretariatsError(response.errors))
    }
  }

  static *onGetUserClassifiedsByRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.getUserClassifiedsRequest>) {
    const user: User = yield select(selectors.auth.user)

    const response = yield* ApiSagas.rest(
      getByUser({ ...payload, user: user.id })
    )

    if (response.data) {
      yield put(actions.classifieds.getUserClassifiedsSuccess(response.data))
    } else {
      yield put(actions.classifieds.getUserClassifiedsError(response.errors))
    }
  }

  static *onGetBlockSchemaRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.getBlockSchemaRequest>) {
    const response = yield* ApiSagas.rest(getBlockSchema(payload))

    if (response.data) {
      yield put(actions.classifieds.getBlockSchemaSuccess(response.data))
    } else {
      yield put(actions.classifieds.getBlockSchemaError(response.errors))
    }
  }

  static *onGetClassifiedSchemaRequest() {
    const response = yield* ApiSagas.rest(
      GetContentTypeSchema({ uid: 'api::classified.classified' })
    )

    if (response.data) {
      yield put(actions.classifieds.getClassifiedSchemaSuccess(response.data))
    } else {
      yield put(actions.classifieds.getClassifiedSchemaError(response.errors))
    }
  }

  static *onGetMediaListRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.getMediaListRequest>) {
    const response = yield* ApiSagas.rest(getMediaList(payload))

    if (response.data) {
      yield put(actions.classifieds.getMediaListSuccess(response.data))
    } else {
      yield put(actions.classifieds.getMediaListError(response.errors))
    }
  }

  static *buildMediaData({
    values,
    classified,
    errorAction,
  }: {
    values: ReturnType<
      typeof actions.classifieds.addMediaToGalleryRequest
    >['payload']['values']
    classified: ReturnType<
      typeof actions.classifieds.addMediaToGalleryRequest
    >['payload']['classified']
    errorAction:
      | typeof actions.classifieds.addMediaToGalleryError
      | typeof actions.classifieds.updateMediaError
  }) {
    const type = values.media?.type

    const data: any = {
      type,
      title: values.title,
      description: values.description,
      ...(type === MediaType.Video
        ? {
            videoUrl: values.media?.video,
          }
        : {}),
    }

    const files = [
      ...(type === MediaType.Document &&
      values.media?.document &&
      !isEmptyFile(values.media.document)
        ? [{ name: 'files.document', value: values.media.document }]
        : []),
      ...(type === MediaType.Photo &&
      values.media?.photo &&
      !isEmptyFile(values.media.photo)
        ? [{ name: 'files.photo', value: values.media.photo }]
        : []),
    ]

    for (const file of files) {
      const uploadResponse = yield* ApiSagas.rest(upload({ files: file.value }))
      const uploadedId = getStrapiUploadFirstFileId(uploadResponse?.data)
      if (!uploadedId) {
        yield put(
          errorAction(
            uploadResponse?.errors ?? [{ message: 'File upload failed' }]
          )
        )
        return null
      }
      const path = file.name.replace(/^files\./, '').split('.')
      let cursor = data
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i]
        cursor[key] = { ...(cursor[key] ?? {}) }
        cursor = cursor[key]
      }
      cursor[path[path.length - 1]] = uploadedId
    }

    if (classified.id != null && classified.id !== '') {
      data.classified = strapiRelationConnect(classified.id)
    }

    return data
  }

  static *onAddMediaToGallery({
    payload,
  }: ReturnType<typeof actions.classifieds.addMediaToGalleryRequest>) {
    const { values, classified } = payload

    const data = yield* ClassifiedsSagas.buildMediaData({
      values,
      classified,
      errorAction: actions.classifieds.addMediaToGalleryError,
    })
    if (!data) return

    const response = yield* ApiSagas.rest(addMedia({ data }))

    if (response.data) {
      yield put(actions.classifieds.addMediaToGallerySuccess(response.data))
    } else {
      yield put(actions.classifieds.addMediaToGalleryError(response.errors))
    }
  }

  static *onUpdateMediaRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.updateMediaRequest>) {
    const { values, classified, id } = payload

    const data = yield* ClassifiedsSagas.buildMediaData({
      values,
      classified,
      errorAction: actions.classifieds.updateMediaError,
    })
    if (!data) return

    const response = yield* ApiSagas.rest(updateMedia({ id, data }))

    if (response.data) {
      yield put(actions.classifieds.updateMediaSuccess(response.data))
    } else {
      yield put(actions.classifieds.updateMediaError(response.errors))
    }
  }

  static *onDeleteMediaRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.deleteMediaRequest>) {
    const response = yield* ApiSagas.rest(deleteMedia(payload))

    if (!response.errors) {
      yield put(actions.classifieds.deleteMediaSuccess(response.data))
    } else {
      yield put(actions.classifieds.deleteMediaReset(response.errors))
    }
  }

  static *onPublishRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.publishRequest>) {
    const response = yield* ApiSagas.rest(publish(payload))

    if (response.data) {
      yield put(actions.classifieds.publishSuccess(response.data))
    } else {
      yield put(actions.classifieds.publishError(response.errors))
    }
  }

  static *onIncreaseViewRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.increaseViewRequest>) {
    const response = yield* ApiSagas.rest(increaseView(payload))

    if (response.data) {
      yield put(actions.classifieds.increaseViewSuccess(response.data))
    } else {
      yield put(actions.classifieds.increaseViewError(response.errors))
    }
  }

  static *onGetKeywordsRequest({
    payload,
  }: ReturnType<typeof actions.classifieds.getKeywordsRequest>) {
    const response = yield* ApiSagas.rest(getKeywords(payload.description))

    if (response.data) {
      yield put(actions.classifieds.getKeywordsSuccess(response.data))
    } else {
      yield put(actions.classifieds.getKeywordsError(response.errors))
    }
  }

  static *loop() {
    yield all([
      takeLatest(actions.classifieds.createRequest, this.onCreateRequest),
      takeLatest(actions.classifieds.updateRequest, this.onUpdateRequest),
      takeLatest(
        actions.classifieds.getClassifiedByIdRequest,
        this.onGetClassifiedById
      ),
      takeLatest(
        actions.classifieds.getClassifiedsRequest,
        this.onGetClassifieds
      ),
      takeLatest(
        actions.classifieds.getClassifiedsWithMetaRequest,
        this.onGetClassifiedsWithMeta
      ),
      takeLatest(
        actions.classifieds.getPublishAdsRequest,
        this.onGetPublishAds
      ),
      takeLatest(actions.classifieds.archiveRequest, this.onArchiveRequest),
      takeLatest(actions.classifieds.hideRequest, this.onHideRequest),
      takeLatest(actions.classifieds.removeRequest, this.onRemoveRequest),
      takeLatest(
        actions.classifieds.getLatestClassifiedsByCategoryRequest,
        this.onGetLatestClassifiedsByCategoryByRequest
      ),
      takeLatest(
        actions.classifieds.getCategoriesRequest,
        this.onGetCategoriesRequest
      ),
      takeLatest(
        actions.classifieds.getCategoriesByUserProfileRequest,
        this.onGetCategoriesByUserProfileRequest
      ),
      takeLatest(
        actions.classifieds.getCabinetTypesRequest,
        this.onGetCabinetTypesRequest
      ),
      takeLatest(
        actions.classifieds.getSecretariatsRequest,
        this.onGetSecretariatsRequests
      ),
      takeLatest(
        actions.classifieds.getOfferTypesRequest,
        this.onGetOfferTypesRequest
      ),
      takeLatest(
        actions.classifieds.getSpecialtiesRequest,
        this.onGetSpecialtiesRequest
      ),
      takeLatest(
        actions.classifieds.getUserClassifiedsRequest,
        this.onGetUserClassifiedsByRequest
      ),
      takeLatest(
        actions.classifieds.getBlockSchemaRequest,
        this.onGetBlockSchemaRequest
      ),
      takeLatest(
        actions.classifieds.addMediaToGalleryRequest,
        this.onAddMediaToGallery
      ),
      takeLatest(
        actions.classifieds.getMediaListRequest,
        this.onGetMediaListRequest
      ),
      takeLatest(
        actions.classifieds.deleteMediaRequest,
        this.onDeleteMediaRequest
      ),
      takeLatest(
        actions.classifieds.updateMediaRequest,
        this.onUpdateMediaRequest
      ),
      takeLatest(actions.classifieds.publishRequest, this.onPublishRequest),
      takeLatest(
        actions.classifieds.getClassifiedSchemaRequest,
        this.onGetClassifiedSchemaRequest
      ),
      takeLatest(
        actions.classifieds.increaseViewRequest,
        this.onIncreaseViewRequest
      ),
      takeLatest(
        actions.classifieds.getKeywordsRequest,
        this.onGetKeywordsRequest
      ),
    ])
  }
}
